// Заявки (04 §9) — the search queue that feeds discovery.
//
// Each row shows what it has actually produced, because that is the only honest basis for turning
// one off. A query that has run five times and found two new businesses is costing Places calls
// for nothing, and the owner should be able to see that at a glance rather than infer it.
//
// Since the matrix (plan §4.4) a row also carries where it came from: its vertical, its tier, the
// batch the wizard created it in, and whether it is a draft nobody has approved yet.

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BatchPanel } from './BatchPanel.js';
import {
  dueByVertical,
  dueState,
  STATE_LABEL,
  stateOf,
  summarizeBatches,
  shortBatch,
  type BatchSummary,
  type QueryState,
} from './queriesModel.js';
import { useSearchQueries, useToggleQuery, type QueryRow } from './useAdmin.js';
import { useVerticals } from './verticals/useVerticals.js';

const TIER_SHORT: Record<string, string> = {
  core: 'ядро',
  core_area: 'ядро × район',
  service: 'услуги',
  service_area: 'услуги × район',
  intent: 'намерение',
  en: 'англ.',
};

interface Filters {
  vertical: string;
  state: '' | QueryState;
  batch: string | null;
}

function matches(row: QueryRow, f: Filters): boolean {
  if (f.vertical !== '' && row.vertical_key !== f.vertical) return false;
  if (f.state !== '' && stateOf(row) !== f.state) return false;
  if (f.batch !== null && row.batch_id !== f.batch) return false;
  return true;
}

function FilterBar({
  filters,
  onChange,
  verticals,
  batches,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  verticals: { key: string; name_bg: string }[];
  batches: BatchSummary[];
}) {
  return (
    <div className="adm__filters">
      <select
        className="adm__select"
        value={filters.vertical}
        onChange={(e) => onChange({ ...filters, vertical: e.target.value })}
        aria-label="Ниша"
      >
        <option value="">Всички ниши</option>
        {verticals.map((v) => (
          <option key={v.key} value={v.key}>
            {v.name_bg}
          </option>
        ))}
      </select>
      <select
        className="adm__select"
        value={filters.state}
        onChange={(e) => onChange({ ...filters, state: e.target.value as Filters['state'] })}
        aria-label="Състояние"
      >
        <option value="">Всички състояния</option>
        {(Object.keys(STATE_LABEL) as QueryState[]).map((state) => (
          <option key={state} value={state}>
            {STATE_LABEL[state]}
          </option>
        ))}
      </select>
      {batches.length > 0 ? (
        <select
          className="adm__select"
          value={filters.batch ?? ''}
          onChange={(e) => onChange({ ...filters, batch: e.target.value || null })}
          aria-label="Партида"
        >
          <option value="">Всички партиди</option>
          {batches.map((b) => (
            <option key={b.batch_id} value={b.batch_id}>
              {shortBatch(b.batch_id)} · {b.vertical_key} · {b.total}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

function QueryTableRow({
  row,
  verticalName,
  onToggle,
}: {
  row: QueryRow;
  verticalName: string;
  onToggle: (active: boolean) => void;
}) {
  const state = stateOf(row);
  const due = state === 'active' ? dueState(row) : null;
  return (
    <tr className={`adm__query-row ${row.active ? '' : 'adm__row--off'}`}>
      <td className="adm__query-text">
        <code>{row.query_text}</code>
      </td>
      <td>{verticalName}</td>
      <td>
        <span className="adm__area-tag">{row.area_hint ?? 'Цял град'}</span>
      </td>
      <td className="adm__sub">{row.tier === null ? '—' : (TIER_SHORT[row.tier] ?? row.tier)}</td>
      <td className="adm__num">{row.runs}</td>
      <td className="adm__num">
        <strong>{row.total_found ?? 0}</strong>
      </td>
      <td className="adm__num adm__num--new">+{row.new_found ?? 0}</td>
      <td className="adm__num">
        {row.cumulative_new}{' '}
        {row.zero_streak >= 2 ? (
          <span className="adm__zero-badge" title="Последователни пускания без нов обект">
            0 нови в {row.zero_streak} пускания
          </span>
        ) : null}
      </td>
      <td>
        <span className={`adm__state adm__state--${state}`}>{STATE_LABEL[state]}</span>
      </td>
      <td>
        {due === null ? (
          <span className="adm__sub">не се пуска</span>
        ) : (
          <span className={`adm__due-badge ${due.due ? 'adm__due-badge--now' : ''}`}>
            {due.text}
          </span>
        )}
      </td>
      <td>
        <label className="adm__toggle-label">
          <input
            type="checkbox"
            className="adm__toggle-check"
            checked={row.active}
            onChange={(event) => onToggle(event.target.checked)}
            aria-label={`Активна: ${row.query_text}`}
          />
          <span className="adm__toggle-state">{row.active ? 'Активна' : 'Спряна'}</span>
        </label>
      </td>
    </tr>
  );
}

export function QueriesEditor() {
  const { data: queries = [], isLoading } = useSearchQueries();
  const { data: verticals = [] } = useVerticals();
  const toggle = useToggleQuery();
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => ({
    vertical: '',
    state: '',
    batch: params.get('batch'),
  }));

  const names = useMemo(() => new Map(verticals.map((v) => [v.key, v.name_bg])), [verticals]);
  const verticalName = (key: string) => names.get(key) ?? key;
  const batches = useMemo(() => summarizeBatches(queries), [queries]);
  const due = useMemo(() => dueByVertical(queries), [queries]);
  const visible = queries.filter((row) => matches(row, filters));

  if (isLoading) return <p className="adm__msg">Зареждане на заявки за търсене…</p>;
  if (queries.length === 0) return <p className="adm__msg">Няма дефинирани заявки за търсене.</p>;

  const dueText = [...due.entries()].map(([key, n]) => `${verticalName(key)} ${n}`).join(' · ');

  return (
    <div className="adm__queries-wrap">
      <div className="adm__section-header">
        <h3 className="adm__h">Заявки за откриване на обекти</h3>
        <span className="adm__sub">
          Автоматични търсения в Google Places и OSM по райони · предстоящи тази нощ:{' '}
          {dueText === '' ? 'няма' : dueText}
        </span>
      </div>

      <BatchPanel
        batches={batches}
        verticalName={verticalName}
        selected={filters.batch}
        onSelect={(batch) => setFilters({ ...filters, batch })}
      />

      <FilterBar filters={filters} onChange={setFilters} verticals={verticals} batches={batches} />
      <p className="adm__sub">
        {visible.length} от {queries.length} заявки
      </p>

      <div className="adm__tablewrap">
        <table className="adm__table">
          <thead>
            <tr>
              <th>заявка</th>
              <th>ниша</th>
              <th>район</th>
              <th>ниво</th>
              <th className="adm__num">пускания</th>
              <th className="adm__num">намерени</th>
              <th className="adm__num">нови</th>
              <th className="adm__num">общо нови</th>
              <th>състояние</th>
              <th>следващо пускане</th>
              <th>активна</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <QueryTableRow
                key={row.id}
                row={row}
                verticalName={verticalName(row.vertical_key)}
                onToggle={(active) => toggle.mutate({ id: row.id, active })}
              />
            ))}
          </tbody>
        </table>
      </div>
      {toggle.isError ? <p className="adm__err">{(toggle.error as Error).message}</p> : null}
    </div>
  );
}
